import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import strategyDocumentService from "../services/strategyDocumentService";
import {
  StrategyDocumentParsedData,
  StrategyDocumentSection,
} from "../types/strategyDocument";

const CONTENT_TYPES = [
  "Original Content",
  "News",
  "Community Content",
  "Jobs",
  "Events",
];

const FORMATS = [
  { value: "short_original", label: "Short Original" },
  { value: "short_summary", label: "Short Summary" },
  { value: "link_dump", label: "Link Dump" },
  { value: "testimonials", label: "Testimonials" },
  { value: "featured_job", label: "Featured Job" },
];

const FREQUENCIES = ["daily", "weekly", "biweekly", "monthly"];

const DAYS_OF_WEEK = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const StrategyDocumentImport: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetBlueprintId = searchParams.get("blueprintId");
  const isOverwriteMode = !!targetBlueprintId;
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1 state
  const [rawText, setRawText] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [parseStatusMessage, setParseStatusMessage] = useState("");
  const [parseError, setParseError] = useState("");
  const [strategyDocumentId, setStrategyDocumentId] = useState<number | null>(null);

  // Step 2 state
  const [parsedData, setParsedData] = useState<StrategyDocumentParsedData | null>(null);

  // Step 3 state
  const [createdBlueprintId, setCreatedBlueprintId] = useState<string | null>(null);

  const wordCount = rawText.trim().split(/\s+/).filter(Boolean).length;
  const charCount = rawText.length;

  const handleParse = async () => {
    if (!rawText.trim()) return;

    setIsParsing(true);
    setParseError("");
    setParseStatusMessage("Submitting document...");

    try {
      const { job_id, strategy_document_id } = await strategyDocumentService.create(rawText);
      setStrategyDocumentId(strategy_document_id);
      setParseStatusMessage("Parsing strategy document...");

      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const status = await strategyDocumentService.pollStatus(job_id);
          setParseStatusMessage(status.message);

          if (status.status === "completed") {
            clearInterval(pollInterval);
            const doc = await strategyDocumentService.get(strategy_document_id);
            setParsedData(doc.parsed_data);
            setIsParsing(false);
            setStep(2);
          } else if (status.status === "failed") {
            clearInterval(pollInterval);
            setIsParsing(false);
            setParseError(status.message || "Failed to parse document.");
          }
        } catch (err) {
          clearInterval(pollInterval);
          setIsParsing(false);
          setParseError("Error polling for status.");
        }
      }, 2000);
    } catch (err: any) {
      setIsParsing(false);
      setParseError(err?.response?.data?.error || "Failed to submit document.");
    }
  };

  const handleSectionChange = (
    index: number,
    field: keyof StrategyDocumentSection,
    value: string
  ) => {
    if (!parsedData) return;
    const updated = [...parsedData.sections];
    updated[index] = { ...updated[index], [field]: value };
    setParsedData({ ...parsedData, sections: updated });
  };

  const handleRemoveSection = (index: number) => {
    if (!parsedData) return;
    const updated = parsedData.sections.filter((_, i) => i !== index);
    setParsedData({ ...parsedData, sections: updated });
  };

  const handleAddSection = () => {
    if (!parsedData) return;
    const newSection: StrategyDocumentSection = {
      title: "New Section",
      content_type: "Original Content",
      format: "short_original",
      instructions: "",
    };
    setParsedData({
      ...parsedData,
      sections: [...parsedData.sections, newSection],
    });
  };

  const handleCreateBlueprint = async () => {
    if (!parsedData || !strategyDocumentId) return;

    try {
      let blueprint;
      if (isOverwriteMode && targetBlueprintId) {
        blueprint = await strategyDocumentService.overwriteBlueprint(
          strategyDocumentId,
          targetBlueprintId,
          parsedData
        );
      } else {
        blueprint = await strategyDocumentService.createBlueprint(
          strategyDocumentId,
          parsedData
        );
      }
      setCreatedBlueprintId(blueprint.id);
      setStep(3);
    } catch (err: any) {
      setParseError(err?.response?.data?.error || "Failed to save blueprint.");
    }
  };

  return (
    <div>
      <h1>{isOverwriteMode ? "Overwrite Blueprint from Strategy Document" : "Import Strategy Document"}</h1>
      <p style={{ marginTop: 0, textAlign: "left" }}>
        {isOverwriteMode
          ? "Paste your strategy document below. AI will extract the blueprint structure, and it will overwrite the existing blueprint."
          : "Paste your strategy document below. AI will extract the blueprint structure for you to review and edit."}
      </p>

      {/* Step 1: Input */}
      {step === 1 && (
        <div>
          <textarea
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder="Paste your strategy document here..."
            style={{
              width: "100%",
              minHeight: "300px",
              padding: "12px",
              fontSize: "14px",
              fontFamily: "inherit",
              borderRadius: "8px",
              border: "1px solid #ddd",
              resize: "vertical",
              boxSizing: "border-box",
            }}
            disabled={isParsing}
            maxLength={50000}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "8px",
              fontSize: "13px",
              color: "#888",
            }}
          >
            <span>
              {wordCount} words / {charCount.toLocaleString()} characters
            </span>
            <span>Max 50,000 characters</span>
          </div>

          {parseError && (
            <div
              style={{
                marginTop: "12px",
                padding: "12px",
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                color: "#dc2626",
                fontSize: "14px",
              }}
            >
              {parseError}
            </div>
          )}

          <div style={{ marginTop: "16px" }}>
            {isParsing ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  className="spinner"
                  style={{
                    width: "20px",
                    height: "20px",
                    border: "3px solid #e5e7eb",
                    borderTop: "3px solid #6366f1",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
                <span style={{ fontSize: "14px", color: "#666" }}>
                  {parseStatusMessage}
                </span>
              </div>
            ) : (
              <button
                className="primary-button"
                onClick={handleParse}
                disabled={!rawText.trim()}
              >
                Parse Document
              </button>
            )}
          </div>

          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Step 2: Review & Edit */}
      {step === 2 && parsedData && (
        <div>
          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: "4px" }}>
              Newsletter Name
            </label>
            <input
              type="text"
              value={parsedData.name}
              onChange={(e) =>
                setParsedData({ ...parsedData, name: e.target.value })
              }
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: "14px",
                borderRadius: "6px",
                border: "1px solid #ddd",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: "4px" }}>
              Audience
            </label>
            <textarea
              value={parsedData.audience}
              onChange={(e) =>
                setParsedData({ ...parsedData, audience: e.target.value })
              }
              rows={3}
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: "14px",
                borderRadius: "6px",
                border: "1px solid #ddd",
                resize: "vertical",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: "4px" }}>
              Additional Details (tone, style, voice)
            </label>
            <textarea
              value={parsedData.additional_details}
              onChange={(e) =>
                setParsedData({
                  ...parsedData,
                  additional_details: e.target.value,
                })
              }
              rows={3}
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: "14px",
                borderRadius: "6px",
                border: "1px solid #ddd",
                resize: "vertical",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: "4px" }}>
                Frequency
              </label>
              <select
                value={parsedData.cadence.frequency}
                onChange={(e) =>
                  setParsedData({
                    ...parsedData,
                    cadence: { ...parsedData.cadence, frequency: e.target.value },
                  })
                }
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  fontSize: "14px",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                }}
              >
                {FREQUENCIES.map((f) => (
                  <option key={f} value={f}>
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {(parsedData.cadence.frequency === "weekly" ||
              parsedData.cadence.frequency === "biweekly") && (
              <div style={{ flex: 1 }}>
                <label style={{ display: "block", fontWeight: 600, marginBottom: "4px" }}>
                  Day of Week
                </label>
                <select
                  value={parsedData.cadence.day_of_week}
                  onChange={(e) =>
                    setParsedData({
                      ...parsedData,
                      cadence: {
                        ...parsedData.cadence,
                        day_of_week: e.target.value,
                      },
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    fontSize: "14px",
                    borderRadius: "6px",
                    border: "1px solid #ddd",
                  }}
                >
                  {DAYS_OF_WEEK.map((d) => (
                    <option key={d} value={d}>
                      {d.charAt(0).toUpperCase() + d.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div style={{ marginBottom: "16px" }}>
            <h2 style={{ marginBottom: "12px" }}>Sections</h2>

            {parsedData.sections.map((section, index) => (
              <div
                key={index}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "12px",
                  backgroundColor: "#fafafa",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: "14px" }}>
                    Section {index + 1}
                  </span>
                  <button
                    onClick={() => handleRemoveSection(index)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#dc2626",
                      cursor: "pointer",
                      fontSize: "13px",
                    }}
                  >
                    Remove
                  </button>
                </div>

                <div style={{ marginBottom: "8px" }}>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "2px" }}>
                    Title
                  </label>
                  <input
                    type="text"
                    value={section.title}
                    onChange={(e) =>
                      handleSectionChange(index, "title", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: "6px 10px",
                      fontSize: "14px",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    marginBottom: "8px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <label style={{ display: "block", fontSize: "13px", marginBottom: "2px" }}>
                      Content Type
                    </label>
                    <select
                      value={section.content_type}
                      onChange={(e) =>
                        handleSectionChange(index, "content_type", e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "6px 10px",
                        fontSize: "14px",
                        borderRadius: "6px",
                        border: "1px solid #ddd",
                      }}
                    >
                      {CONTENT_TYPES.map((ct) => (
                        <option key={ct} value={ct}>
                          {ct}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ flex: 1 }}>
                    <label style={{ display: "block", fontSize: "13px", marginBottom: "2px" }}>
                      Format
                    </label>
                    <select
                      value={section.format}
                      onChange={(e) =>
                        handleSectionChange(index, "format", e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "6px 10px",
                        fontSize: "14px",
                        borderRadius: "6px",
                        border: "1px solid #ddd",
                      }}
                    >
                      {FORMATS.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "2px" }}>
                    Instructions
                  </label>
                  <textarea
                    value={section.instructions}
                    onChange={(e) =>
                      handleSectionChange(index, "instructions", e.target.value)
                    }
                    rows={3}
                    style={{
                      width: "100%",
                      padding: "6px 10px",
                      fontSize: "14px",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                      resize: "vertical",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
            ))}

            <button
              className="tertiary-info-button"
              onClick={handleAddSection}
              style={{ marginTop: "8px" }}
            >
              + Add Section
            </button>
          </div>

          {parseError && (
            <div
              style={{
                marginTop: "12px",
                padding: "12px",
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                color: "#dc2626",
                fontSize: "14px",
              }}
            >
              {parseError}
            </div>
          )}

          <div style={{ marginTop: "24px" }}>
            <button
              className="primary-button"
              onClick={handleCreateBlueprint}
            >
              {isOverwriteMode ? "Overwrite Blueprint" : "Create Blueprint"}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Result */}
      {step === 3 && createdBlueprintId && (
        <div
          style={{
            textAlign: "center",
            padding: "48px 0",
          }}
        >
          <h2>{isOverwriteMode ? "Blueprint Updated" : "Blueprint Created"}</h2>
          <p style={{ color: "#666", marginBottom: "24px" }}>
            {isOverwriteMode
              ? "Your blueprint has been overwritten with the strategy document data."
              : "Your strategy document has been converted into a blueprint."}
          </p>
          <button
            className="primary-button"
            onClick={() => navigate(`/blueprints/${createdBlueprintId}`)}
          >
            View Blueprint
          </button>
        </div>
      )}
    </div>
  );
};

export default StrategyDocumentImport;
